import React from 'react';

import { SearchFilters } from '../api/courses';
import { GRADE_LEVELS, GRADE_LEVEL_LABELS, SUBJECTS } from '../domain/catalog';
import { SORT_VALUES } from '../domain/catalog-context';

const SORT_LABELS: Record<NonNullable<SearchFilters['sortBy']>, string> = {
  soonest: 'Próxima disponibilidad',
  newest: 'Mas recientes',
  price_low: 'Menor precio',
  price_high: 'Mayor precio',
};

export interface CatalogFiltersProps {
  value: SearchFilters;
  onChange: (next: SearchFilters) => void;
}

const field: React.CSSProperties = { marginBottom: 16 };

const label: React.CSSProperties = { display: 'block', marginBottom: 4, fontWeight: 600 };

const control: React.CSSProperties = {
  width: '100%',
  padding: 8,
  borderRadius: 4,
  border: '1px solid #d1d5da',
};

/**
 * The catalog's filter controls — rendered once, wherever they are shown.
 *
 * PRT-BR-P03-16 asks for mobile filter semantics equal to desktop. Two copies
 * of a `<select>` list is how they stop being equal: the phone's panel keeps
 * the option somebody removed from the sidebar, and both are labelled
 * "Materia" so a screen reader finds two of every filter on the page. One
 * component, rendered in one place at a time, is what makes "equal" a fact
 * about the code rather than a promise about maintenance.
 *
 * It owns no state. The caller decides when a change is committed, which is
 * what lets the phone stage edits behind Apply while the sidebar filters live.
 */
export const CatalogFilters: React.FC<CatalogFiltersProps> = ({ value, onChange }) => {
  const set = (key: keyof SearchFilters, next: unknown) =>
    onChange({ ...value, [key]: next || undefined });

  return (
    <div>
      <div style={field}>
        <label style={label} htmlFor="catalog-subject">
          Materia
        </label>
        <select
          id="catalog-subject"
          value={value.subject || ''}
          onChange={(e) => set('subject', e.target.value)}
          style={control}
        >
          <option value="">Todas</option>
          {SUBJECTS.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      <div style={field}>
        <label style={label} htmlFor="catalog-grade">
          Grado
        </label>
        <select
          id="catalog-grade"
          value={value.level || ''}
          onChange={(e) => set('level', e.target.value)}
          style={control}
        >
          <option value="">Todos</option>
          {GRADE_LEVELS.map((g) => (
            <option key={g} value={g}>
              {GRADE_LEVEL_LABELS[g]}
            </option>
          ))}
        </select>
      </div>

      <div style={field}>
        <label style={label} htmlFor="catalog-sort">
          Ordenar por
        </label>
        <select
          id="catalog-sort"
          value={value.sortBy || 'trust'}
          onChange={(e) => set('sortBy', e.target.value)}
          style={control}
        >
          {SORT_VALUES.map((v) => (
            <option key={v} value={v}>
              {SORT_LABELS[v]}
            </option>
          ))}
        </select>
      </div>

      <div style={field}>
        <label style={label} htmlFor="catalog-max-price">
          Precio máximo
        </label>
        <input
          id="catalog-max-price"
          type="number"
          inputMode="numeric"
          min={0}
          value={value.maxPrice ?? ''}
          onChange={(e) => set('maxPrice', e.target.value ? Number(e.target.value) : undefined)}
          style={control}
        />
      </div>
    </div>
  );
};

export default CatalogFilters;
